const _jsxFileName = '';
function _nullishCoalesce(lhs, rhsFn) {
  if (lhs != null) {
    return lhs;
  } else {
    return rhsFn();
  }
}
function _optionalChain(ops) {
  let lastAccessLHS = undefined;
  let value = ops[0];
  let i = 1;
  while (i < ops.length) {
    const op = ops[i];
    const fn = ops[i + 1];
    i += 2;
    if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) {
      return undefined;
    }
    if (op === 'access' || op === 'optionalAccess') {
      lastAccessLHS = value;
      value = fn(value);
    } else if (op === 'call' || op === 'optionalCall') {
      value = fn((...args) => value.call(lastAccessLHS, ...args));
      lastAccessLHS = undefined;
    }
  }
  return value;
}
import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Truck,
  Lock,
  CheckCircle2,
  Plus,
  Minus,
  Trash2,
  Printer,
  Upload,
  AlertCircle,
  Clock,
  Building,
  Store,
  LogIn,
  UserPlus,
  Sparkles,
} from 'lucide-react';

import { fetchBankDetails, getDefaultBank } from '../services/bankDetails';

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL;
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
});

// Placeholder image
const PLACEHOLDER_IMAGE = 'https://placehold.co/400x400/f5f5f5/999999?text=No+Image';

// ============================================================
// FIXED: repairBase64Image - Fixes malformed base64 strings
// ============================================================
const repairBase64Image = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== 'string') return PLACEHOLDER_IMAGE;

  const trimmed = imageUrl.trim();
  if (!trimmed) return PLACEHOLDER_IMAGE;

  // Fix malformed base64 (missing semicolon or comma)
  if (trimmed.startsWith('data:image/')) {
    let fixed = trimmed;

    // Fix missing semicolon before base64 (data:image/jpeg:base64 -> data:image/jpeg;base64,)
    if (fixed.includes(':base64') && !fixed.includes(';base64')) {
      fixed = fixed.replace(':base64', ';base64,');
    }

    // Fix missing comma after base64
    if (fixed.includes(';base64') && !fixed.includes(';base64,')) {
      fixed = fixed.replace(';base64', ';base64,');
    }

    return fixed;
  }

  // Keep blob URLs
  if (trimmed.startsWith('blob:')) {
    return trimmed;
  }

  // Keep absolute URLs
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  // Handle relative paths
  if (trimmed.startsWith('/')) {
    const SITE_BASE_URL = import.meta.env.VITE_API_URL.replace('/api', '');
    return `${SITE_BASE_URL}${trimmed}`;
  }

  return PLACEHOLDER_IMAGE;
};

// ============================================================
// Image compression utilities
// ============================================================
const compressImage = (base64, maxWidth = 800, quality = 0.6) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = base64;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = (maxWidth / width) * height;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      _optionalChain([
        ctx,
        'optionalAccess',
        (_) => _.drawImage,
        'call',
        (_2) => _2(img, 0, 0, width, height),
      ]);

      const compressed = canvas.toDataURL('image/jpeg', quality);
      resolve(compressed);
    };
    img.onerror = reject;
  });
};

// Helper function to format date for receipt
const formatReceiptDate = (date) => {
  const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  };
  return date.toLocaleString('en-PH', options);
};

// Generate receipt HTML for printing
const generateReceiptHTML = (order, user, profileData) => {
  const now = new Date();
  const orderDate = order.orderDate ? new Date(order.orderDate) : now;
  const isPickup = order.deliveryType === 'pickup' || order.delivery_type === 'pickup';
  const isPaid = order.status === 'Paid';

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Sparkling Gems - Official Receipt</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Courier New', monospace; background: #f5f5f5; display: flex; justify-content: center; align-items: center; min-height: 100vh; padding: 20px; }
        .receipt { max-width: 400px; width: 100%; background: white; border-radius: 12px; box-shadow: 0 10px 40px rgba(0,0,0,0.1); overflow: hidden; }
        .receipt-content { padding: 24px 20px; }
        .header { text-align: center; border-bottom: 1px dashed #ccc; padding-bottom: 16px; margin-bottom: 16px; }
        .store-name { font-size: 20px; font-weight: bold; letter-spacing: 2px; color: #1a1a1a; }
        .store-tagline { font-size: 9px; color: #b4945c; letter-spacing: 1px; margin-top: 4px; }
        .store-address { font-size: 8px; color: #666; margin-top: 8px; line-height: 1.3; }
        .receipt-title { font-size: 11px; font-weight: bold; letter-spacing: 2px; margin-top: 12px; color: #333; }
        .info-section { border-bottom: 1px dashed #eee; padding-bottom: 12px; margin-bottom: 12px; }
        .info-row { display: flex; justify-content: space-between; font-size: 9px; margin-bottom: 4px; font-family: monospace; }
        .info-label { color: #666; }
        .info-value { color: #333; font-weight: 500; }
        .items-header { display: flex; justify-content: space-between; font-size: 9px; font-weight: bold; padding-bottom: 6px; margin-bottom: 8px; border-bottom: 1px solid #ddd; text-transform: uppercase; letter-spacing: 0.5px; }
        .item-row { display: flex; justify-content: space-between; font-size: 9px; margin-bottom: 6px; line-height: 1.3; }
        .item-name { flex: 2; color: #333; word-break: break-word; }
        .item-qty { width: 35px; text-align: center; color: #666; }
        .item-price { width: 70px; text-align: right; color: #333; }
        .totals-section { border-top: 1px dashed #ccc; padding-top: 12px; margin-top: 12px; }
        .total-row { display: flex; justify-content: space-between; font-size: 9px; margin-bottom: 4px; }
        .grand-total { border-top: 1px solid #333; margin-top: 8px; padding-top: 6px; font-weight: bold; font-size: 11px; }
        .payment-method { margin-top: 12px; padding-top: 8px; border-top: 1px dashed #eee; }
        .footer { text-align: center; border-top: 1px dashed #ccc; padding-top: 16px; margin-top: 16px; font-size: 8px; color: #888; }
        .qr-note { margin-top: 12px; font-size: 7px; color: #aaa; text-align: center; }
        .thankyou { font-size: 10px; font-weight: bold; text-align: center; margin-top: 12px; color: #b4945c; }
        @media print { body { background: white; padding: 0; margin: 0; } .receipt { box-shadow: none; max-width: 100%; } .print-button { display: none; } }
        .print-button { display: block; width: calc(100% - 40px); margin: 0 20px 20px 20px; padding: 10px; background: #1a1a1a; color: white; border: none; border-radius: 6px; font-size: 12px; font-weight: bold; cursor: pointer; font-family: monospace; }
        .print-button:hover { background: #b4945c; }
        .status-badge { display: inline-block; background: #f97316; color: white; font-size: 8px; padding: 2px 6px; border-radius: 12px; margin-top: 8px; }
        .status-paid { background: #22c55e; }
      </style>
    </head>
    <body>
      <div class="receipt">
        <div class="receipt-content">
          <div class="header">
            <div class="store-name">✨ SPARKLING GEMS ✨</div>
            <div class="store-tagline">LUXURY • AUTHENTICITY • ELEGANCE</div>
            <div class="store-address">2F, San Antonio Building, Corumi<br>Quezon City, 1012 Metro Manila<br>Tel: (02) 1234 5678 | VAT REG TIN: 123-456-789-000</div>
            <div class="receipt-title">OFFICIAL RECEIPT</div>
          </div>
          
          <div class="info-section">
            <div class="info-row"><span class="info-label">RECEIPT NO:</span><span class="info-value">${order.order_number || order.id}</span></div>
            <div class="info-row"><span class="info-label">DATE & TIME:</span><span class="info-value">${formatReceiptDate(orderDate)}</span></div>
            <div class="info-row"><span class="info-label">TRANSACTION ID:</span><span class="info-value">${order.transactionId || 'PENDING'}</span></div>
            <div class="info-row"><span class="info-label">SALES ASSISTANT:</span><span class="info-value">Online System</span></div>
            <div class="info-row"><span class="info-label">POS TERMINAL:</span><span class="info-value">WEB-001</span></div>
            <div class="info-row"><span class="info-label">STATUS:</span><span class="info-value"><span class="status-badge ${isPaid ? 'status-paid' : ''}" style="display: inline-block; background: ${isPaid ? '#22c55e' : '#f97316'}; color: white; padding: 2px 6px; border-radius: 12px;">${isPaid ? 'PAID' : 'PENDING VERIFICATION'}</span></span></div>
          </div>
          
          <div class="info-section">
            <div class="info-row"><span class="info-label">CUSTOMER:</span><span class="info-value">${order.customerName || order.customer_name || 'Guest Customer'}</span></div>
            <div class="info-row"><span class="info-label">EMAIL:</span><span class="info-value">${order.customer_email || profileData.email || 'N/A'}</span></div>
            <div class="info-row"><span class="info-label">CONTACT:</span><span class="info-value">${order.customer_phone || profileData.phone || 'N/A'}</span></div>
            ${
              isPickup
                ? `
            <div class="info-row"><span class="info-label">PICKUP PERSON:</span><span class="info-value">${order.pickup_name || order.customerName}</span></div>
            <div class="info-row"><span class="info-label">PICKUP CONTACT:</span><span class="info-value">${order.pickup_phone || order.customer_phone}</span></div>
            `
                : `
            <div class="info-row"><span class="info-label">DELIVERY ADDRESS:</span><span class="info-value">${order.shippingAddress || order.shipping_address || 'Store Pickup'}</span></div>
            `
            }
          </div>
          
          <div class="items-header"><span>ITEM</span><span>QTY</span><span>AMOUNT</span></div>
          ${
            order.items &&
            order.items
              .map(
                (item) => `
            <div class="item-row">
              <span class="item-name">${(item.name || item.product_name || 'Product').substring(0, 35)}${(item.name || '').length > 35 ? '...' : ''}</span>
              <span class="item-qty">${item.quantity || 1}</span>
              <span class="item-price">₱ ${((item.price || item.product_price || 0) * (item.quantity || 1)).toLocaleString()}</span>
            </div>
            ${item.description ? `<div class="item-row" style="margin-top: -4px;"><span class="item-name" style="font-size: 7px; color: #888;">  ${item.description.substring(0, 40)}${item.description.length > 40 ? '...' : ''}</span><span></span><span></span></div>` : ''}
          `
              )
              .join('')
          }
          
          <div class="totals-section">
            <div class="total-row"><span>Subtotal:</span><span>₱ ${(order.subtotal || order.total || 0).toLocaleString()}</span></div>
            <div class="total-row"><span>Shipping Fee:</span><span>${(order.shipping_fee || 0) > 0 ? `₱ ${(order.shipping_fee || 0).toLocaleString()}` : 'FREE'}</span></div>
            <div class="total-row"><span>Discount:</span><span>₱ 0.00</span></div>
            <div class="total-row"><span>VAT (12%):</span><span>₱ ${((order.total || order.totalAmount || 0) * 0.12).toLocaleString()}</span></div>
            <div class="total-row grand-total"><span>TOTAL AMOUNT DUE:</span><span>₱ ${(order.total || order.totalAmount || 0).toLocaleString()}</span></div>
          </div>
          
          <div class="payment-method">
            <div class="info-row"><span class="info-label">PAYMENT METHOD:</span><span class="info-value">${order.paymentMethod || order.payment_method || 'Bank Transfer'}</span></div>
            ${order.transactionId ? `<div class="info-row"><span class="info-label">REFERENCE NO:</span><span class="info-value">${order.transactionId}</span></div>` : ''}
          </div>
          
          ${
            isPickup
              ? `
          <div class="info-section" style="margin-top: 12px; background: #f9f9f9; padding: 8px;">
            <div class="info-row"><span class="info-label">PICKUP LOCATION:</span><span class="info-value">Sparkling Gems Jewelry Shop</span></div>
            <div class="info-row"><span class="info-label">PICKUP HOURS:</span><span class="info-value">Mon-Sat, 10:00 AM - 7:00 PM</span></div>
            <div class="info-row"><span class="info-label">REQUIREMENTS:</span><span class="info-value">Valid ID & Order Confirmation</span></div>
          </div>
          `
              : `
          <div class="info-section" style="margin-top: 12px; background: #f9f9f9; padding: 8px;">
            <div class="info-row"><span class="info-label">TRACKING NUMBER:</span><span class="info-value">${order.trackingNumber || order.tracking_number || 'To be assigned'}</span></div>
            <div class="info-row"><span class="info-label">ESTIMATED DELIVERY:</span><span class="info-value">3-5 business days after shipment</span></div>
          </div>
          `
          }
          
          <div class="footer">
            <div>This is a system-generated official receipt.</div>
            <div>For inquiries, contact concierge@sparklinggems.ph</div>
            <div>Terms and conditions apply.</div>
            <div class="qr-note">⚡ Verified by Sparkling Gems Security System ⚡</div>
            <div class="thankyou">✨ THANK YOU FOR SHOPPING WITH US! ✨</div>
            <div style="margin-top: 8px; font-size: 7px;">Visit us at sparklinggems.shop</div>
          </div>
        </div>
        <button class="print-button" onclick="window.print();">🖨️ PRINT RECEIPT</button>
      </div>
      <script>window.onload = function() { setTimeout(() => { window.print(); }, 500); };</script>
    </body>
    </html>
  `;
};

// Print receipt function
const printReceipt = (order, user, profileData) => {
  const receiptHTML = generateReceiptHTML(order, user, profileData);
  const printWindow = window.open('', '_blank', 'width=500,height=700');
  if (printWindow) {
    printWindow.document.write(receiptHTML);
    printWindow.document.close();
  }
};

const Checkout = ({
  cart,
  onPlaceOrder,
  onNavigate,
  onSelectProduct,
  onUpdateCartQty,
  onRemoveFromCart,
  onClearCart,
  directCheckoutItem: propDirectCheckoutItem,
  user,
  onRefreshWishlist,
}) => {
  const [step, setStep] = useState('form');
  const [isProcessing, setIsProcessing] = useState(false);
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };
  const [completedOrder, setCompletedOrder] = useState(null);
  const [paymentProof, setPaymentProof] = useState(null);
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [userId, setUserId] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);

  // Guest checkout modal state
  const [showGuestModal, setShowGuestModal] = useState(false);

  // Profile data for receipt
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
  });

  // Delivery type state
  const [deliveryType, setDeliveryType] = useState('shipping');

  // Bank details state
  const [bankDetails, setBankDetails] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [loadingBanks, setLoadingBanks] = useState(true);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    address: '',
    city: '',
    postalCode: '',
    phone: '',
    paymentMethod: 'bank',
  });

  // Pickup form data
  const [pickupData, setPickupData] = useState({
    name: '',
    phone: '',
    email: '',
  });

  // Local direct checkout item (for recovery)
  const [localDirectCheckoutItem, setLocalDirectCheckoutItem] = useState(
    propDirectCheckoutItem || null
  );

  // Check if user is logged in
  const isLoggedIn = !!user && !!user.id;

  const isWishlistCheckout = () => {
    try {
      const wishlistCheckout = sessionStorage.getItem('wishlistCheckout');
      if (!wishlistCheckout) return false;
      const parsed = JSON.parse(wishlistCheckout);
      return (
        _optionalChain([parsed, 'optionalAccess', (_3) => _3.source]) === 'wishlist' &&
        Array.isArray(_optionalChain([parsed, 'optionalAccess', (_4) => _4.items])) &&
        parsed.items.length > 0
      );
    } catch (error) {
      return false;
    }
  };

  // Get wishlist product IDs for removal
  const getWishlistProductIds = () => {
    try {
      const wishlistCheckout = sessionStorage.getItem('wishlistCheckout');
      if (!wishlistCheckout) return [];
      const parsed = JSON.parse(wishlistCheckout);
      if (
        _optionalChain([parsed, 'optionalAccess', (_5) => _5.source]) === 'wishlist' &&
        Array.isArray(_optionalChain([parsed, 'optionalAccess', (_6) => _6.items]))
      ) {
        return parsed.items.map((item) => item.product.id);
      }
    } catch (error) {}
    return [];
  };

  // Remove items from wishlist after successful checkout
  const removeFromWishlist = async (productIds) => {
    if (!productIds.length) return;

    const currentUserId = _optionalChain([user, 'optionalAccess', (_7) => _7.id]) || userId;
    const guestId = localStorage.getItem('guest_id');

    if (!currentUserId && !guestId) return;

    try {
      const response = await fetch(`${API_BASE_URL}/wishlist/remove-bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: currentUserId ? parseInt(String(currentUserId)) : null,
          guest_id: guestId || null,
          product_ids: productIds,
        }),
      });

      const data = await response.json();
      if (data.success) {
        if (onRefreshWishlist) {
          onRefreshWishlist();
        }
        window.dispatchEvent(new CustomEvent('wishlist-updated'));
      }
    } catch (error) {}
  };

  // Show guest modal if not logged in and trying to checkout
  useEffect(() => {
    if (!isLoggedIn && (cart.length > 0 || localDirectCheckoutItem)) {
      setShowGuestModal(true);
    }
  }, [isLoggedIn, cart.length, localDirectCheckoutItem]);

  // Handle continue as guest - just go back to shop
  const handleContinueAsGuest = () => {
    setShowGuestModal(false);
    onNavigate('shop');
  };

  // Handle login click
  const handleLoginClick = () => {
    setShowGuestModal(false);
    onNavigate('auth');
  };

  // Handle register click
  const handleRegisterClick = () => {
    setShowGuestModal(false);
    onNavigate('register');
  };

  // Load profile data for receipt
  useEffect(() => {
    if (_optionalChain([user, 'optionalAccess', (_8) => _8.id])) {
      loadProfileForReceipt();
    }
  }, [user]);

  const loadProfileForReceipt = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/users/get-profile?user_id=${_optionalChain([user, 'optionalAccess', (_9) => _9.id])}`,
        { headers: authHeaders() }
      );
      const data = await response.json();
      if (data.success && data.user) {
        setProfileData({
          firstName: data.user.firstName || '',
          lastName: data.user.lastName || '',
          email: data.user.email || '',
          phone: data.user.phone || '',
          address: data.user.address || '',
          city: data.user.city || '',
          postalCode: data.user.postalCode || '',
        });
      }
    } catch (error) {}
  };

  // Check for stored checkout data on component mount (for page reload recovery)
  useEffect(() => {
    const storedCheckout = sessionStorage.getItem('directCheckout');
    if (storedCheckout && !propDirectCheckoutItem && cart.length === 0) {
      try {
        const parsed = JSON.parse(storedCheckout);
        setLocalDirectCheckoutItem({
          product: parsed.product,
          quantity: parsed.quantity,
        });
        sessionStorage.removeItem('directCheckout');
      } catch (e) {}
    }
  }, [propDirectCheckoutItem, cart.length]);

  // Load user profile and set userId from user prop - WITH AUTOFILL
  useEffect(() => {
    const loadUserProfile = async () => {
      setIsLoadingProfile(true);
      try {
        let currentUserId = _optionalChain([user, 'optionalAccess', (_10) => _10.id]) || null;

        if (!currentUserId) {
          const userStr = localStorage.getItem('user');
          if (userStr) {
            const parsedUser = JSON.parse(userStr);
            currentUserId = parsedUser.id;
          }
        }

        if (!currentUserId) {
          const storedUserId = localStorage.getItem('userId');
          if (storedUserId) {
            currentUserId = parseInt(storedUserId);
          }
        }

        if (!currentUserId) {
          setUserId(null);
          setIsLoadingProfile(false);
          return;
        }

        setUserId(currentUserId);

        const response = await fetch(`${API_BASE_URL}/users/get-profile?user_id=${currentUserId}`, {
          headers: authHeaders(),
        });
        const data = await response.json();

        if (data.success && data.user) {
          const profile = data.user;

          setFormData((prev) => ({
            ...prev,
            firstName: profile.firstName || profile.first_name || '',
            lastName: profile.lastName || profile.last_name || '',
            email:
              profile.email || _optionalChain([user, 'optionalAccess', (_11) => _11.email]) || '',
            phone:
              profile.phone || _optionalChain([user, 'optionalAccess', (_12) => _12.phone]) || '',
            address: profile.address || '',
            city: profile.city || '',
            postalCode: profile.postalCode || profile.postal_code || '',
          }));

          const fullName =
            `${profile.firstName || profile.first_name || ''} ${profile.lastName || profile.last_name || ''}`.trim();
          setPickupData((prev) => ({
            ...prev,
            name: fullName || _optionalChain([user, 'optionalAccess', (_13) => _13.name]) || '',
            phone:
              profile.phone || _optionalChain([user, 'optionalAccess', (_14) => _14.phone]) || '',
            email:
              profile.email || _optionalChain([user, 'optionalAccess', (_15) => _15.email]) || '',
          }));

          setProfileData({
            firstName: profile.firstName || '',
            lastName: profile.lastName || '',
            email: profile.email || '',
            phone: profile.phone || '',
            address: profile.address || '',
            city: profile.city || '',
            postalCode: profile.postalCode || '',
          });
        } else if (user) {
          setFormData((prev) => ({
            ...prev,
            firstName:
              user.firstName ||
              _optionalChain([
                user,
                'access',
                (_16) => _16.name,
                'optionalAccess',
                (_17) => _17.split,
                'call',
                (_18) => _18(' '),
                'access',
                (_19) => _19[0],
              ]) ||
              '',
            lastName:
              user.lastName ||
              _optionalChain([
                user,
                'access',
                (_20) => _20.name,
                'optionalAccess',
                (_21) => _21.split,
                'call',
                (_22) => _22(' '),
                'access',
                (_23) => _23.slice,
                'call',
                (_24) => _24(1),
                'access',
                (_25) => _25.join,
                'call',
                (_26) => _26(' '),
              ]) ||
              '',
            email: user.email || '',
            phone: user.phone || '',
          }));

          setPickupData((prev) => ({
            ...prev,
            name: user.name || '',
            phone: user.phone || '',
            email: user.email || '',
          }));

          setProfileData({
            firstName:
              user.firstName ||
              _optionalChain([
                user,
                'access',
                (_27) => _27.name,
                'optionalAccess',
                (_28) => _28.split,
                'call',
                (_29) => _29(' '),
                'access',
                (_30) => _30[0],
              ]) ||
              '',
            lastName:
              user.lastName ||
              _optionalChain([
                user,
                'access',
                (_31) => _31.name,
                'optionalAccess',
                (_32) => _32.split,
                'call',
                (_33) => _33(' '),
                'access',
                (_34) => _34.slice,
                'call',
                (_35) => _35(1),
                'access',
                (_36) => _36.join,
                'call',
                (_37) => _37(' '),
              ]) ||
              '',
            email: user.email || '',
            phone: user.phone || '',
            address: '',
            city: '',
            postalCode: '',
          });
        }
      } catch (error) {
      } finally {
        setIsLoadingProfile(false);
      }
    };

    loadUserProfile();
  }, [user]);

  // State to force re-render when cart updates
  const [cartUpdateKey, setCartUpdateKey] = useState(0);

  // Monitor cart changes and recalculate totals
  useEffect(() => {
    // Force a re-render when cart changes
    setCartUpdateKey((prev) => prev + 1);
  }, [cart]);

  // Determine which items to display
  const displayItems = localDirectCheckoutItem
    ? [{ ...localDirectCheckoutItem.product, quantity: localDirectCheckoutItem.quantity }]
    : cart;

  const subtotal = displayItems.reduce((acc, item) => {
    const itemPrice = item.price || 0;
    const itemQty = item.quantity || 1;
    return acc + itemPrice * itemQty;
  }, 0);
  const shipping = 0;
  const total = subtotal + shipping;

  // Fetch bank details on component mount
  useEffect(() => {
    const loadBankDetails = async () => {
      setLoadingBanks(true);
      const banks = await fetchBankDetails();
      setBankDetails(banks);
      const defaultBank = getDefaultBank(banks);
      if (defaultBank) setSelectedBank(defaultBank);
      setLoadingBanks(false);
    };
    loadBankDetails();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError(null);
  };

  const handlePickupChange = (e) => {
    setPickupData({ ...pickupData, [e.target.name]: e.target.value });
    if (error) setError(null);
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (deliveryType === 'shipping') {
      setFormData({ ...formData, phone: value });
    } else {
      setPickupData({ ...pickupData, phone: value });
    }
    if (error) setError(null);
  };

  const handleFileUpload = (e) => {
    const file = _optionalChain([
      e,
      'access',
      (_38) => _38.target,
      'access',
      (_39) => _39.files,
      'optionalAccess',
      (_40) => _40[0],
    ]);
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file (JPG, PNG)');
        return;
      }
      setPaymentProofFile(file);
      const url = URL.createObjectURL(file);
      setPaymentProof(url);
      setError(null);
    }
  };

  const updateProductStock = async (productId, quantity) => {
    try {
      const response = await fetch(`${API_BASE_URL}/products/update-stock`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ product_id: productId, quantity: quantity, operation: 'decrease' }),
      });
      const data = await response.json();
    } catch (error) {}
  };

  const clearCartFromDatabase = async () => {
    const storedUserId = localStorage.getItem('userId');
    const guestId = localStorage.getItem('guest_id');
    if (!storedUserId && !guestId) return;
    try {
      await fetch(`${API_BASE_URL}/cart/clear`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          user_id: storedUserId ? parseInt(storedUserId) : null,
          guest_id: guestId || null,
        }),
      });
    } catch (error) {}
  };

  const validateForm = () => {
    if (!formData.email.trim()) return 'Email is required';
    if (!formData.email.includes('@')) return 'Valid email is required';

    const phone = deliveryType === 'shipping' ? formData.phone : pickupData.phone;
    if (!phone.trim()) return 'Phone number is required';
    if (phone.length < 10) return 'Phone number must be at least 10 digits';

    if (deliveryType === 'shipping') {
      if (!formData.firstName.trim()) return 'First name is required';
      if (!formData.lastName.trim()) return 'Last name is required';
      if (!formData.address.trim()) return 'Address is required';
      if (!formData.city.trim()) return 'City is required';
      if (!formData.postalCode.trim()) return 'Postal code is required';
    } else {
      if (!pickupData.name.trim()) return 'Name is required for pickup';
    }

    if (!paymentProofFile)
      return 'Please upload a proof of payment (screenshot or receipt) to proceed.';
    if (!selectedBank) return 'Bank details are loading. Please wait or refresh the page.';
    return null;
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // handleSubmit function
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isLoggedIn) {
      setShowGuestModal(true);
      return;
    }

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsProcessing(true);
    setError(null);
    setUploadProgress(30);

    try {
      let paymentProofBase64 = await fileToBase64(paymentProofFile);
      const originalSize = paymentProofBase64.length;

      if (originalSize > 1 * 1024 * 1024) {
        let compressed = await compressImage(paymentProofBase64, 800, 0.6);
        let newSize = compressed.length;

        if (newSize > 1.5 * 1024 * 1024) {
          compressed = await compressImage(paymentProofBase64, 700, 0.5);
          newSize = compressed.length;
        }

        if (newSize > 2 * 1024 * 1024) {
          compressed = await compressImage(paymentProofBase64, 600, 0.4);
          newSize = compressed.length;
        }

        paymentProofBase64 = compressed;
      }

      if (paymentProofBase64.length > 3 * 1024 * 1024) {
        throw new Error(
          'Image is too large. Please upload a smaller image (max 3MB after compression).'
        );
      }

      setUploadProgress(50);
      const transactionId = `TXN-${Date.now().toString().slice(-8)}-${Math.floor(Math.random() * 1000)}`;

      let finalUserId = userId;
      if (!finalUserId && _optionalChain([user, 'optionalAccess', (_41) => _41.id]))
        finalUserId = user.id;

      const orderData = {
        user_id: finalUserId,
        customer: {
          firstName:
            deliveryType === 'shipping' ? formData.firstName : pickupData.name.split(' ')[0] || '',
          lastName:
            deliveryType === 'shipping'
              ? formData.lastName
              : pickupData.name.split(' ').slice(1).join(' ') || '',
          email: formData.email,
          address: deliveryType === 'shipping' ? formData.address : 'Store Pickup',
          city: deliveryType === 'shipping' ? formData.city : 'Makati',
          postalCode: deliveryType === 'shipping' ? formData.postalCode : '1200',
          phone: deliveryType === 'shipping' ? formData.phone : pickupData.phone,
        },
        items: displayItems.map((item) => ({
          product_id: item.id,
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          image: repairBase64Image(item.image),
          category: item.category || null,
          description: item.description || null,
          // Add jewelry-specific fields
          weight: item.weight,
          karat: item.karat,
          goldType: item.goldType,
          condition: item.condition,
        })),
        paymentMethod: 'Bank Transfer',
        deliveryType: deliveryType,
        subtotal,
        shipping,
        total,
        transactionId,
        orderDate: new Date().toISOString(),
        tracking_number:
          deliveryType === 'shipping' ? `TRK-${Date.now().toString().slice(-8)}` : null,
      };

      if (deliveryType === 'pickup') {
        orderData.pickup_name = pickupData.name;
        orderData.pickup_phone = pickupData.phone;
        orderData.pickup_email = formData.email;
      }

      setUploadProgress(70);

      const orderResponse = await fetch(`${API_BASE_URL}/orders/create`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(orderData),
      });

      const orderResponseText = await orderResponse.text();
      let orderResult;
      try {
        orderResult = JSON.parse(orderResponseText);
      } catch (e) {
        throw new Error('Invalid response from server');
      }

      if (!orderResult.success) throw new Error(orderResult.error || 'Failed to create order');

      const orderId = orderResult.order_id;
      setUploadProgress(85);

      const proofData = {
        order_id: orderId,
        payment_proof: paymentProofBase64,
        transaction_id: transactionId,
        user_id: finalUserId,
      };

      const proofResponse = await fetch(`${API_BASE_URL}/orders/upload-proof`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(proofData),
      });

      const proofResult = await proofResponse.json();

      if (!proofResult.success) {
        showToast(
          `Warning: ${proofResult.error}. Your order was created but proof may not be saved. Please contact support with order #${orderResult.order_number || orderId}.`,
          'error'
        );
      }

      for (const item of displayItems) {
        await updateProductStock(item.id, item.quantity);
      }

      const wishlistCheckoutActive = isWishlistCheckout();

      if (wishlistCheckoutActive) {
        const productIds = getWishlistProductIds();
        if (productIds.length > 0) {
          await removeFromWishlist(productIds);
          sessionStorage.removeItem('wishlistCheckout');
        }
      }

      if (!wishlistCheckoutActive) {
        await clearCartFromDatabase();
      }

      setUploadProgress(100);

      const newOrder = {
        id: orderResult.order_number || `SG-${orderId}`,
        order_number: orderResult.order_number || `SG-${orderId}`,
        userId: finalUserId || 1,
        customerName:
          deliveryType === 'shipping'
            ? `${formData.firstName} ${formData.lastName}`
            : pickupData.name,
        customer_name:
          deliveryType === 'shipping'
            ? `${formData.firstName} ${formData.lastName}`
            : pickupData.name,
        customer_email: formData.email,
        customer_phone: deliveryType === 'shipping' ? formData.phone : pickupData.phone,
        date: new Date().toLocaleDateString(),
        total: total,
        totalAmount: total,
        subtotal: subtotal,
        shipping_fee: shipping,
        status: 'Pending',
        paymentMethod: 'Bank Transfer',
        payment_method: 'Bank Transfer',
        deliveryType: deliveryType,
        transactionId: transactionId,
        transaction_id: transactionId,
        shippingAddress:
          deliveryType === 'shipping'
            ? `${formData.address}, ${formData.city} ${formData.postalCode}`
            : 'Store Pickup - Sparkling Gems Jewelry Shop',
        shipping_address:
          deliveryType === 'shipping'
            ? `${formData.address}, ${formData.city} ${formData.postalCode}`
            : 'Store Pickup - Sparkling Gems Jewelry Shop',
        city: deliveryType === 'shipping' ? formData.city : 'Makati',
        postal_code: deliveryType === 'shipping' ? formData.postalCode : '1200',
        items: displayItems.map((c) => ({
          id: c.id,
          product_id: c.id,
          name: c.name,
          description: c.description || '',
          price: c.price,
          image: repairBase64Image(c.image),
          category: c.category,
          quantity: c.quantity,
          // Preserve jewelry-specific fields
          weight: c.weight,
          karat: c.karat,
          goldType: c.goldType,
          condition: c.condition,
        })),
        paymentProof: paymentProofBase64,
        payment_proof: paymentProofBase64,
        trackingNumber: orderData.tracking_number || undefined,
        tracking_number: orderData.tracking_number || undefined,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        file_path: undefined,
      };

      if (deliveryType === 'pickup') {
        newOrder.pickup_name = pickupData.name;
        newOrder.pickup_phone = pickupData.phone;
        newOrder.pickup_email = formData.email;
      }

      onPlaceOrder(newOrder);

      // Save address to profile only if profile has no address yet (avoid overwriting Settings)
      if (
        user &&
        user.id &&
        deliveryType === 'shipping' &&
        formData.address &&
        !profileData.address
      ) {
        fetch(`${API_BASE_URL}/users/update-profile`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            user_id: user.id,
            address: formData.address,
            city: formData.city,
            postal_code: formData.postalCode,
          }),
        }).catch(() => {});
      }

      if (!wishlistCheckoutActive && onClearCart) {
        onClearCart();
      }

      if (!wishlistCheckoutActive) {
        localStorage.removeItem('cartCount');
        localStorage.removeItem('cart');
        window.dispatchEvent(new Event('storage'));
      }

      setCompletedOrder(newOrder);
      setStep('success');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Connection error. Please try again.');
    } finally {
      setIsProcessing(false);
      setUploadProgress(0);
    }
  };

  // Success page component
  if (step === 'success' && completedOrder) {
    return React.createElement(
      'div',
      {
        className: 'min-h-screen bg-stone-50 flex items-center justify-center pt-24 pb-12 px-6',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 912 },
      },
      React.createElement(
        'div',
        {
          className:
            'max-w-2xl w-full bg-white border border-stone-200 shadow-2xl rounded-sm animate-fade-in relative overflow-hidden',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 913 },
        },
        React.createElement('div', {
          className: `h-2 w-full bg-gradient-to-r ${completedOrder.status === 'Pending' ? 'from-orange-300 via-orange-500 to-orange-300' : 'from-gold-300 via-gold-500 to-gold-300'}`,
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 914 },
        }),
        React.createElement(
          'div',
          {
            className: 'p-8 md:p-12',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 915 },
          },
          React.createElement(
            'div',
            {
              className: 'text-center border-b border-stone-100 pb-8 mb-8',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 916 },
            },
            React.createElement(
              'div',
              {
                className: `w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 ${completedOrder.status === 'Pending' ? 'bg-orange-50 border-orange-200 text-orange-600' : 'bg-green-50 border-green-200 text-green-600'}`,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 917 },
              },
              completedOrder.status === 'Pending'
                ? React.createElement(Clock, {
                    size: 40,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 918 },
                  })
                : React.createElement(CheckCircle2, {
                    size: 40,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 918 },
                  })
            ),
            React.createElement(
              'h2',
              {
                className: 'font-serif text-3xl text-stone-900 mb-2',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 920 },
              },
              completedOrder.status === 'Pending'
                ? 'Payment Pending Verification'
                : 'Payment Confirmed'
            ),
            React.createElement(
              'p',
              {
                className: 'text-stone-500 text-sm max-w-md mx-auto',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 923 },
              },
              completedOrder.status === 'Pending'
                ? 'We have received your proof of payment. Our concierge team will verify your transaction within 24 hours.'
                : 'Thank you for your purchase. A receipt has been sent to your email.'
            ),
            React.createElement(
              'div',
              {
                className:
                  'mt-4 inline-flex items-center gap-2 px-3 py-1 bg-stone-100 rounded-full',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 928 },
              },
              React.createElement(
                'span',
                {
                  className: 'text-xs font-medium',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 929 },
                },
                completedOrder.deliveryType === 'pickup' ? '📍 Pickup Order' : '🚚 Shipping Order'
              )
            )
          ),
          React.createElement(
            'div',
            {
              className: 'flex gap-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 932 },
            },
            React.createElement(
              'button',
              {
                onClick: () =>
                  printReceipt(
                    completedOrder,
                    _nullishCoalesce(user, () => null),
                    profileData
                  ),
                className:
                  'flex-1 flex items-center justify-center gap-2 border border-stone-200 py-3 text-stone-600 text-xs uppercase tracking-widest hover:border-stone-900 hover:text-stone-900 transition-colors rounded-sm',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 933 },
              },

              React.createElement(Printer, {
                size: 16,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 937 },
              }),
              ' Print Receipt'
            ),
            React.createElement(
              'button',
              {
                onClick: () => onNavigate('shop'),
                className:
                  'flex-1 bg-stone-900 text-white py-3 text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors rounded-sm',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 939 },
              },
              'Continue Shopping'
            )
          )
        )
      )
    );
  }

  // Empty state
  if (!localDirectCheckoutItem && cart.length === 0 && !isWishlistCheckout() && step === 'form') {
    return React.createElement(
      'div',
      {
        className: 'min-h-screen bg-stone-50 pt-32 pb-20 px-6 text-center',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 952 },
      },
      React.createElement(
        'h2',
        {
          className: 'font-serif text-3xl text-stone-900 mb-4',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 953 },
        },
        'Your Bag is Empty'
      ),
      React.createElement(
        'p',
        {
          className: 'text-stone-500 mb-8',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 954 },
        },
        "It looks like you haven't added any treasures yet."
      ),
      React.createElement(
        'button',
        {
          onClick: () => onNavigate('shop'),
          className:
            'bg-stone-900 text-white px-8 py-3 uppercase text-xs tracking-[0.2em] hover:bg-gold-600 transition-colors rounded-sm',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 955 },
        },
        'Continue Shopping'
      )
    );
  }

  // Loading state
  if (isLoadingProfile) {
    return React.createElement(
      'div',
      {
        className: 'min-h-screen bg-stone-50 pt-32 pb-20 flex items-center justify-center',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 965 },
      },
      React.createElement(
        'div',
        {
          className: 'text-center',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 966 },
        },
        React.createElement('div', {
          className:
            'w-10 h-10 border-2 border-gold-600 border-t-transparent rounded-full animate-spin mx-auto mb-4',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 967 },
        }),
        React.createElement(
          'p',
          {
            className: 'text-stone-500 text-sm',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 968 },
          },
          'Loading your information...'
        )
      )
    );
  }

  // If not logged in, show only the modal
  if (!isLoggedIn && (cart.length > 0 || localDirectCheckoutItem)) {
    return React.createElement(
      'div',
      {
        className: 'min-h-screen bg-stone-50 pt-32 pb-20',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 977 },
      },
      React.createElement(
        'div',
        {
          className: 'max-w-7xl mx-auto px-6',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 978 },
        },
        React.createElement(
          'div',
          {
            className: 'fixed inset-0 z-[400] flex items-center justify-center',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 979 },
          },
          React.createElement('div', {
            className: 'absolute inset-0 bg-stone-900/80 backdrop-blur-md',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 980 },
          }),
          React.createElement(
            'div',
            {
              className:
                'relative bg-white w-full max-w-md rounded-sm shadow-2xl mx-4 overflow-hidden animate-fade-up-enter',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 981 },
            },
            React.createElement(
              'div',
              {
                className: 'bg-gradient-to-r from-gold-600 to-gold-500 text-white p-6 text-center',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 982 },
              },
              React.createElement(
                'div',
                {
                  className:
                    'w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 983 },
                },
                React.createElement(Sparkles, {
                  size: 32,
                  className: 'text-white',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 984 },
                })
              ),
              React.createElement(
                'h3',
                {
                  className: 'font-serif text-2xl mb-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 986 },
                },
                'Welcome to Sparkling Gems'
              ),
              React.createElement(
                'p',
                {
                  className: 'text-sm text-white/90',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 987 },
                },
                'Sign in to complete your purchase and unlock exclusive benefits'
              )
            ),

            React.createElement(
              'div',
              {
                className: 'p-8 space-y-6',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 990 },
              },
              React.createElement(
                'div',
                {
                  className: 'bg-amber-50 border border-amber-200 p-4 rounded-sm',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 991 },
                },
                React.createElement(
                  'p',
                  {
                    className: 'text-sm text-amber-800 font-medium mb-2',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 992 },
                  },
                  '✨ Member Benefits ✨'
                ),
                React.createElement(
                  'ul',
                  {
                    className: 'space-y-2 text-xs text-amber-700',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 993 },
                  },
                  React.createElement(
                    'li',
                    {
                      className: 'flex items-center gap-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 994 },
                    },
                    '✓ Track your order status in real-time'
                  ),
                  React.createElement(
                    'li',
                    {
                      className: 'flex items-center gap-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 995 },
                    },
                    '✓ Earn loyalty points (1 point per ₱100 spent)'
                  ),
                  React.createElement(
                    'li',
                    {
                      className: 'flex items-center gap-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 996 },
                    },
                    '✓ Access exclusive member-only collections'
                  ),
                  React.createElement(
                    'li',
                    {
                      className: 'flex items-center gap-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 997 },
                    },
                    '✓ Faster checkout with saved addresses'
                  ),
                  React.createElement(
                    'li',
                    {
                      className: 'flex items-center gap-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 998 },
                    },
                    '✓ Receive order updates via email/SMS'
                  ),
                  React.createElement(
                    'li',
                    {
                      className: 'flex items-center gap-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 999 },
                    },
                    '✓ Priority customer support'
                  )
                )
              ),

              React.createElement(
                'div',
                {
                  className: 'space-y-3',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1003 },
                },
                React.createElement(
                  'button',
                  {
                    onClick: handleLoginClick,
                    className:
                      'w-full bg-stone-900 text-white py-3 rounded-sm hover:bg-gold-600 transition-colors flex items-center justify-center gap-2 text-sm uppercase tracking-wider',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1004 },
                  },

                  React.createElement(LogIn, {
                    size: 18,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1008 },
                  }),
                  'Login to Your Account'
                ),

                React.createElement(
                  'button',
                  {
                    onClick: handleRegisterClick,
                    className:
                      'w-full border border-stone-300 text-stone-700 py-3 rounded-sm hover:bg-stone-50 transition-colors flex items-center justify-center gap-2 text-sm uppercase tracking-wider',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1012 },
                  },

                  React.createElement(UserPlus, {
                    size: 18,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1016 },
                  }),
                  'Create New Account'
                )
              ),

              React.createElement(
                'div',
                {
                  className: 'relative',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1021 },
                },
                React.createElement(
                  'div',
                  {
                    className: 'absolute inset-0 flex items-center',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1022 },
                  },
                  React.createElement('div', {
                    className: 'w-full border-t border-stone-200',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1023 },
                  })
                ),
                React.createElement(
                  'div',
                  {
                    className: 'relative flex justify-center text-xs uppercase',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1025 },
                  },
                  React.createElement(
                    'span',
                    {
                      className: 'bg-white px-4 text-stone-400',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1026 },
                    },
                    'or'
                  )
                )
              ),

              React.createElement(
                'button',
                {
                  onClick: handleContinueAsGuest,
                  className:
                    'w-full text-stone-500 py-2 text-sm hover:text-stone-700 transition-colors flex items-center justify-center gap-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1030 },
                },
                'Continue Browsing'
              ),

              React.createElement(
                'p',
                {
                  className: 'text-center text-[10px] text-stone-400 mt-4',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1037 },
                },
                'By continuing, you agree to our ',
                React.createElement(
                  'button',
                  {
                    onClick: () => onNavigate('privacy-policy'),
                    className: 'text-gold-600 hover:underline',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1038 },
                  },
                  'Privacy Policy'
                ),
                ' and ',
                React.createElement(
                  'button',
                  {
                    className: 'text-gold-600 hover:underline',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1038 },
                  },
                  'Terms of Service'
                )
              )
            )
          )
        )
      )
    );
  }

  // Main checkout form
  return React.createElement(
    'div',
    {
      className: 'min-h-screen bg-stone-50 pt-32 pb-20',
      __self: this,
      __source: { fileName: _jsxFileName, lineNumber: 1050 },
    },
    toast &&
      React.createElement(
        'div',
        {
          className: `fixed top-6 right-6 z-[999] flex items-center gap-3 px-5 py-4 rounded-sm shadow-xl text-sm font-medium transition-all ${toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-stone-900 text-white'}`,
          style: { minWidth: '300px' },
        },
        React.createElement('span', null, toast.message),
        React.createElement(
          'button',
          {
            onClick: () => setToast(null),
            className: 'ml-auto text-white/70 hover:text-white text-lg leading-none',
          },
          '×'
        )
      ),
    React.createElement(
      'div',
      {
        className: 'max-w-7xl mx-auto px-6',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 1051 },
      },
      React.createElement(
        'button',
        {
          onClick: () => onNavigate('shop'),
          className:
            'flex items-center gap-2 text-stone-500 hover:text-stone-900 text-xs uppercase tracking-widest mb-8 transition-colors',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 1052 },
        },
        React.createElement(ArrowLeft, {
          size: 14,
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 1053 },
        }),
        ' Continue Shopping'
      ),
      React.createElement(
        'h1',
        {
          className: 'font-serif text-4xl text-stone-900 mb-12',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 1055 },
        },
        'Secure Checkout'
      ),

      error &&
        React.createElement(
          'div',
          {
            className:
              'mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm rounded-sm flex items-start gap-2',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 1058 },
          },
          React.createElement(AlertCircle, {
            size: 16,
            className: 'flex-shrink-0 mt-0.5',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 1059 },
          }),
          React.createElement(
            'span',
            { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1060 } },
            error
          )
        ),

      isProcessing &&
        React.createElement(
          'div',
          {
            className: 'mb-6 p-4 bg-blue-50 border border-blue-200 rounded-sm',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 1065 },
          },
          React.createElement(
            'div',
            {
              className: 'flex items-center justify-between mb-2',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1066 },
            },
            React.createElement(
              'span',
              {
                className: 'text-sm text-blue-700',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1067 },
              },
              'Processing your order...'
            ),
            React.createElement(
              'span',
              {
                className: 'text-xs text-blue-600',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1068 },
              },
              uploadProgress,
              '%'
            )
          ),
          React.createElement(
            'div',
            {
              className: 'w-full bg-blue-200 rounded-full h-2',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1070 },
            },
            React.createElement('div', {
              className: 'bg-blue-600 h-2 rounded-full transition-all duration-300',
              style: { width: `${uploadProgress}%` },
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1071 },
            })
          )
        ),

      React.createElement(
        'div',
        {
          className: 'flex flex-col lg:flex-row gap-12 lg:gap-24',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 1076 },
        },

        React.createElement(
          'div',
          {
            className: 'flex-1',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 1078 },
          },
          React.createElement(
            'form',
            {
              id: 'checkout-form',
              onSubmit: handleSubmit,
              className: 'space-y-12',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1079 },
            },

            React.createElement(
              'section',
              { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1081 } },
              React.createElement(
                'div',
                {
                  className: 'flex items-center gap-2 mb-6 border-b border-stone-200 pb-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1082 },
                },
                React.createElement(Truck, {
                  size: 18,
                  className: 'text-gold-600',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1083 },
                }),
                React.createElement(
                  'h3',
                  {
                    className: 'font-serif text-xl text-stone-900',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1084 },
                  },
                  'Delivery Method'
                )
              ),

              React.createElement(
                'div',
                {
                  className: 'grid grid-cols-1 md:grid-cols-2 gap-4 mb-6',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1087 },
                },
                React.createElement(
                  'button',
                  {
                    type: 'button',
                    onClick: () => setDeliveryType('shipping'),
                    className: `p-6 border-2 rounded-sm transition-all flex flex-col items-center gap-3 ${deliveryType === 'shipping' ? 'border-gold-600 bg-gold-50' : 'border-stone-200 hover:border-gold-300'}`,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1088 },
                  },
                  React.createElement(Truck, {
                    size: 32,
                    className: deliveryType === 'shipping' ? 'text-gold-600' : 'text-stone-400',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1089 },
                  }),
                  React.createElement(
                    'span',
                    {
                      className: `font-bold ${deliveryType === 'shipping' ? 'text-gold-600' : 'text-stone-600'}`,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1090 },
                    },
                    'Ship to Address'
                  ),
                  React.createElement(
                    'span',
                    {
                      className: 'text-xs text-stone-500',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1091 },
                    },
                    'Home or office delivery'
                  )
                ),
                React.createElement(
                  'button',
                  {
                    type: 'button',
                    onClick: () => setDeliveryType('pickup'),
                    className: `p-6 border-2 rounded-sm transition-all flex flex-col items-center gap-3 ${deliveryType === 'pickup' ? 'border-gold-600 bg-gold-50' : 'border-stone-200 hover:border-gold-300'}`,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1093 },
                  },
                  React.createElement(Store, {
                    size: 32,
                    className: deliveryType === 'pickup' ? 'text-gold-600' : 'text-stone-400',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1094 },
                  }),
                  React.createElement(
                    'span',
                    {
                      className: `font-bold ${deliveryType === 'pickup' ? 'text-gold-600' : 'text-stone-600'}`,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1095 },
                    },
                    'Store Pickup'
                  ),
                  React.createElement(
                    'span',
                    {
                      className: 'text-xs text-stone-500',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1096 },
                    },
                    'Free • Ready in 2-3 days'
                  )
                )
              ),

              deliveryType === 'pickup' &&
                React.createElement(
                  'div',
                  {
                    className: 'bg-amber-50 border border-amber-200 p-4 rounded-sm mb-4',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1101 },
                  },
                  React.createElement(
                    'div',
                    {
                      className: 'flex items-start gap-3',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1102 },
                    },
                    React.createElement(Store, {
                      size: 20,
                      className: 'text-amber-600 mt-0.5',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1103 },
                    }),
                    React.createElement(
                      'div',
                      { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1104 } },
                      React.createElement(
                        'h4',
                        {
                          className: 'font-bold text-amber-800 text-sm',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1105 },
                        },
                        'Sparkling Gems Jewelry Shop'
                      ),
                      React.createElement(
                        'p',
                        {
                          className: 'text-xs text-amber-700 mt-1',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1106 },
                        },
                        '2F, San Antonio Building, Corumi, Quezon City, 1012 Metro Manila'
                      ),
                      React.createElement(
                        'p',
                        {
                          className: 'text-xs text-amber-600 mt-2',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1107 },
                        },
                        '⏰ Pickup Hours: Mon-Sat, 10am - 7pm'
                      )
                    )
                  )
                )
            ),

            deliveryType === 'shipping' &&
              React.createElement(
                'section',
                { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1115 } },
                React.createElement(
                  'div',
                  {
                    className: 'flex items-center gap-2 mb-6 border-b border-stone-200 pb-2',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1116 },
                  },
                  React.createElement(Truck, {
                    size: 18,
                    className: 'text-gold-600',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1117 },
                  }),
                  React.createElement(
                    'h3',
                    {
                      className: 'font-serif text-xl text-stone-900',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1118 },
                    },
                    'Shipping Details'
                  )
                ),

                React.createElement(
                  'div',
                  {
                    className: 'grid grid-cols-1 md:grid-cols-2 gap-6',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1121 },
                  },
                  React.createElement(
                    'div',
                    {
                      className: 'space-y-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1122 },
                    },
                    React.createElement(
                      'label',
                      {
                        className: 'text-[10px] uppercase tracking-widest text-stone-500 font-bold',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1123 },
                      },
                      'First Name *'
                    ),
                    React.createElement('input', {
                      required: true,
                      name: 'firstName',
                      value: formData.firstName,
                      onChange: handleChange,
                      type: 'text',
                      className:
                        'w-full border-b border-stone-300 py-2 bg-transparent focus:border-gold-600 focus:outline-none transition-colors',
                      placeholder: 'e.g., Juan',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1124 },
                    })
                  ),
                  React.createElement(
                    'div',
                    {
                      className: 'space-y-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1126 },
                    },
                    React.createElement(
                      'label',
                      {
                        className: 'text-[10px] uppercase tracking-widest text-stone-500 font-bold',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1127 },
                      },
                      'Last Name *'
                    ),
                    React.createElement('input', {
                      required: true,
                      name: 'lastName',
                      value: formData.lastName,
                      onChange: handleChange,
                      type: 'text',
                      className:
                        'w-full border-b border-stone-300 py-2 bg-transparent focus:border-gold-600 focus:outline-none transition-colors',
                      placeholder: 'e.g., Dela Cruz',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1128 },
                    })
                  ),
                  React.createElement(
                    'div',
                    {
                      className: 'space-y-2 md:col-span-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1130 },
                    },
                    React.createElement(
                      'label',
                      {
                        className: 'text-[10px] uppercase tracking-widest text-stone-500 font-bold',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1131 },
                      },
                      'Email Address *'
                    ),
                    React.createElement('input', {
                      required: true,
                      name: 'email',
                      value: formData.email,
                      onChange: handleChange,
                      type: 'email',
                      className:
                        'w-full border-b border-stone-300 py-2 bg-transparent focus:border-gold-600 focus:outline-none transition-colors',
                      placeholder: 'your@email.com',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1132 },
                    })
                  ),
                  React.createElement(
                    'div',
                    {
                      className: 'space-y-2 md:col-span-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1134 },
                    },
                    React.createElement(
                      'label',
                      {
                        className: 'text-[10px] uppercase tracking-widest text-stone-500 font-bold',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1135 },
                      },
                      'Street Address *'
                    ),
                    React.createElement('input', {
                      required: true,
                      name: 'address',
                      value: formData.address,
                      onChange: handleChange,
                      type: 'text',
                      className:
                        'w-full border-b border-stone-300 py-2 bg-transparent focus:border-gold-600 focus:outline-none transition-colors',
                      placeholder: '123 Street Name',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1136 },
                    })
                  ),
                  React.createElement(
                    'div',
                    {
                      className: 'space-y-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1138 },
                    },
                    React.createElement(
                      'label',
                      {
                        className: 'text-[10px] uppercase tracking-widest text-stone-500 font-bold',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1139 },
                      },
                      'City *'
                    ),
                    React.createElement('input', {
                      required: true,
                      name: 'city',
                      value: formData.city,
                      onChange: handleChange,
                      type: 'text',
                      className:
                        'w-full border-b border-stone-300 py-2 bg-transparent focus:border-gold-600 focus:outline-none transition-colors',
                      placeholder: 'Makati',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1140 },
                    })
                  ),
                  React.createElement(
                    'div',
                    {
                      className: 'space-y-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1142 },
                    },
                    React.createElement(
                      'label',
                      {
                        className: 'text-[10px] uppercase tracking-widest text-stone-500 font-bold',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1143 },
                      },
                      'Postal Code *'
                    ),
                    React.createElement('input', {
                      required: true,
                      name: 'postalCode',
                      value: formData.postalCode,
                      onChange: handleChange,
                      type: 'text',
                      className:
                        'w-full border-b border-stone-300 py-2 bg-transparent focus:border-gold-600 focus:outline-none transition-colors',
                      placeholder: '1200',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1144 },
                    })
                  ),
                  React.createElement(
                    'div',
                    {
                      className: 'space-y-2 md:col-span-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1146 },
                    },
                    React.createElement(
                      'label',
                      {
                        className: 'text-[10px] uppercase tracking-widest text-stone-500 font-bold',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1147 },
                      },
                      'Phone *'
                    ),
                    React.createElement('input', {
                      required: true,
                      name: 'phone',
                      value: formData.phone,
                      onChange: handlePhoneChange,
                      type: 'tel',
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      placeholder: 'e.g. 09171234567',
                      maxLength: 11,
                      className:
                        'w-full border-b border-stone-300 py-2 bg-transparent focus:border-gold-600 focus:outline-none transition-colors',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1148 },
                    })
                  )
                ),
                React.createElement(
                  'div',
                  {
                    className: 'mt-4 text-xs text-stone-400 italic',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1151 },
                  },
                  'Your information has been auto-filled from your profile. You can edit if needed.'
                )
              ),

            deliveryType === 'pickup' &&
              React.createElement(
                'section',
                { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1158 } },
                React.createElement(
                  'div',
                  {
                    className: 'flex items-center gap-2 mb-6 border-b border-stone-200 pb-2',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1159 },
                  },
                  React.createElement(Store, {
                    size: 18,
                    className: 'text-gold-600',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1160 },
                  }),
                  React.createElement(
                    'h3',
                    {
                      className: 'font-serif text-xl text-stone-900',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1161 },
                    },
                    'Pickup Information'
                  )
                ),
                React.createElement(
                  'div',
                  {
                    className: 'grid grid-cols-1 gap-6',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1163 },
                  },
                  React.createElement(
                    'div',
                    {
                      className: 'space-y-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1164 },
                    },
                    React.createElement(
                      'label',
                      {
                        className: 'text-[10px] uppercase tracking-widest text-stone-500 font-bold',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1165 },
                      },
                      'Full Name *'
                    ),
                    React.createElement('input', {
                      required: true,
                      name: 'name',
                      value: pickupData.name,
                      onChange: handlePickupChange,
                      type: 'text',
                      placeholder: 'Person picking up the order',
                      className:
                        'w-full border-b border-stone-300 py-2 bg-transparent focus:border-gold-600 focus:outline-none transition-colors',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1166 },
                    })
                  ),
                  React.createElement(
                    'div',
                    {
                      className: 'space-y-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1168 },
                    },
                    React.createElement(
                      'label',
                      {
                        className: 'text-[10px] uppercase tracking-widest text-stone-500 font-bold',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1169 },
                      },
                      'Phone Number *'
                    ),
                    React.createElement('input', {
                      required: true,
                      name: 'phone',
                      value: pickupData.phone,
                      onChange: handlePhoneChange,
                      type: 'tel',
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      placeholder: 'e.g. 09171234567',
                      maxLength: 11,
                      className:
                        'w-full border-b border-stone-300 py-2 bg-transparent focus:border-gold-600 focus:outline-none transition-colors',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1170 },
                    })
                  ),
                  React.createElement(
                    'div',
                    {
                      className: 'space-y-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1172 },
                    },
                    React.createElement(
                      'label',
                      {
                        className: 'text-[10px] uppercase tracking-widest text-stone-500 font-bold',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1173 },
                      },
                      'Email Address *'
                    ),
                    React.createElement('input', {
                      required: true,
                      name: 'email',
                      value: formData.email,
                      onChange: handleChange,
                      type: 'email',
                      placeholder: 'For order confirmation',
                      className:
                        'w-full border-b border-stone-300 py-2 bg-transparent focus:border-gold-600 focus:outline-none transition-colors',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1174 },
                    })
                  ),
                  React.createElement(
                    'div',
                    {
                      className: 'bg-stone-50 p-4 rounded-sm border border-stone-200',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1176 },
                    },
                    React.createElement(
                      'p',
                      {
                        className: 'text-xs text-stone-600',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1177 },
                      },
                      React.createElement(
                        'span',
                        {
                          className: 'font-bold',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1177 },
                        },
                        'Note:'
                      ),
                      ' Please bring a valid ID when picking up your order. You will receive an email notification when your order is ready for pickup.'
                    )
                  )
                )
              ),

            React.createElement(
              'section',
              { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1183 } },
              React.createElement(
                'div',
                {
                  className: 'flex items-center gap-2 mb-6 border-b border-stone-200 pb-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1184 },
                },
                React.createElement(Building, {
                  size: 18,
                  className: 'text-gold-600',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1185 },
                }),
                React.createElement(
                  'h3',
                  {
                    className: 'font-serif text-xl text-stone-900',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1186 },
                  },
                  'Bank Transfer Payment'
                )
              ),

              React.createElement(
                'div',
                { className: 'space-y-6' },
                loadingBanks
                  ? React.createElement(
                      'div',
                      {
                        className: 'text-center py-4',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1191 },
                      },
                      React.createElement('div', {
                        className:
                          'w-6 h-6 border-2 border-gold-600 border-t-transparent rounded-full animate-spin mx-auto mb-2',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1191 },
                      }),
                      React.createElement(
                        'p',
                        {
                          className: 'text-xs text-stone-500',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1191 },
                        },
                        'Loading bank details...'
                      )
                    )
                  : bankDetails.length === 0
                    ? React.createElement(
                        'div',
                        {
                          className: 'bg-red-50 border border-red-200 rounded-lg p-4 text-center',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1193 },
                        },
                        React.createElement(AlertCircle, {
                          className: 'w-8 h-8 text-red-400 mx-auto mb-2',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1193 },
                        }),
                        React.createElement(
                          'p',
                          {
                            className: 'text-sm text-red-600',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1193 },
                          },
                          'Bank details temporarily unavailable'
                        )
                      )
                    : React.createElement(
                        React.Fragment,
                        null,
                        bankDetails.length > 1 &&
                          React.createElement(
                            'div',
                            {
                              className: 'mb-4',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1197 },
                            },
                            React.createElement(
                              'label',
                              {
                                className:
                                  'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1197 },
                              },
                              'Select Bank'
                            ),
                            React.createElement(
                              'select',
                              {
                                value: _optionalChain([
                                  selectedBank,
                                  'optionalAccess',
                                  (_42) => _42.bankName,
                                ]),
                                onChange: (e) => {
                                  const bank = bankDetails.find(
                                    (b) => b.bankName === e.target.value
                                  );
                                  setSelectedBank(bank || null);
                                },
                                className:
                                  'w-full p-3 border border-stone-300 rounded-sm focus:ring-2 focus:ring-gold-600 focus:border-transparent',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1198 },
                              },
                              bankDetails.map((bank) =>
                                React.createElement(
                                  'option',
                                  {
                                    key: bank.bankName,
                                    value: bank.bankName,
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1199 },
                                  },
                                  bank.bankName
                                )
                              )
                            )
                          ),
                        selectedBank &&
                          React.createElement(
                            'div',
                            {
                              className: 'bg-white p-4 border border-stone-200 rounded-sm mb-4',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1203 },
                            },
                            React.createElement(
                              'p',
                              {
                                className: 'text-xs text-stone-500 uppercase tracking-widest mb-2',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1204 },
                              },
                              'Bank Details'
                            ),
                            React.createElement(
                              'div',
                              {
                                className: 'grid grid-cols-2 gap-4 mb-3',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1205 },
                              },
                              React.createElement(
                                'div',
                                {
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1205 },
                                },
                                React.createElement(
                                  'p',
                                  {
                                    className: 'text-[9px] uppercase text-stone-400 mb-1',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1205 },
                                  },
                                  'Bank'
                                ),
                                React.createElement(
                                  'p',
                                  {
                                    className: 'text-sm font-bold text-stone-900',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1205 },
                                  },
                                  selectedBank.bankName
                                )
                              ),
                              React.createElement(
                                'div',
                                {
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1205 },
                                },
                                React.createElement(
                                  'p',
                                  {
                                    className: 'text-[9px] uppercase text-stone-400 mb-1',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1205 },
                                  },
                                  'Currency'
                                ),
                                React.createElement(
                                  'p',
                                  {
                                    className: 'text-sm font-bold text-stone-900',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1205 },
                                  },
                                  selectedBank.currency
                                )
                              )
                            ),
                            React.createElement(
                              'div',
                              {
                                className: 'mb-3',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1206 },
                              },
                              React.createElement(
                                'p',
                                {
                                  className: 'text-[9px] uppercase text-stone-400 mb-1',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1206 },
                                },
                                'Account Number'
                              ),
                              React.createElement(
                                'p',
                                {
                                  className: 'font-mono text-base font-bold text-stone-900',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1206 },
                                },
                                selectedBank.accountNumber
                              )
                            ),
                            React.createElement(
                              'div',
                              {
                                className: 'mb-3',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1207 },
                              },
                              React.createElement(
                                'p',
                                {
                                  className: 'text-[9px] uppercase text-stone-400 mb-1',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1207 },
                                },
                                'Account Name'
                              ),
                              React.createElement(
                                'p',
                                {
                                  className: 'text-sm font-bold text-stone-900',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1207 },
                                },
                                selectedBank.accountName
                              )
                            ),
                            React.createElement(
                              'div',
                              {
                                className: 'mt-4 pt-3 border-t border-dashed border-stone-200',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1208 },
                              },
                              React.createElement(
                                'p',
                                {
                                  className: 'text-[9px] uppercase text-stone-400 mb-1',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1208 },
                                },
                                'Amount to Pay'
                              ),
                              React.createElement(
                                'p',
                                {
                                  className: 'text-xl font-bold text-gold-600',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1208 },
                                },
                                '₱ ',
                                total.toLocaleString()
                              )
                            )
                          ),
                        _optionalChain([
                          selectedBank,
                          'optionalAccess',
                          (_43) => _43.instructions,
                        ]) &&
                          React.createElement(
                            'div',
                            {
                              className: 'bg-blue-50 border border-blue-200 p-4 rounded-sm mb-4',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1211 },
                            },
                            React.createElement(
                              'p',
                              {
                                className: 'text-xs text-blue-800 whitespace-pre-line',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1211 },
                              },
                              selectedBank.instructions
                            )
                          )
                      ),

                React.createElement(
                  'div',
                  {
                    className: 'border-t border-stone-200 pt-4',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1215 },
                  },
                  React.createElement(
                    'label',
                    {
                      className:
                        'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-3',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1216 },
                    },
                    'Upload Proof of Payment ',
                    React.createElement(
                      'span',
                      {
                        className: 'text-red-500',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1216 },
                      },
                      '*'
                    )
                  ),
                  paymentProof
                    ? React.createElement(
                        'div',
                        {
                          className: 'mb-3',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1218 },
                        },
                        React.createElement(
                          'div',
                          {
                            className:
                              'flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-sm',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1218 },
                          },
                          React.createElement(CheckCircle2, {
                            size: 20,
                            className: 'text-green-600',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1218 },
                          }),
                          React.createElement(
                            'div',
                            {
                              className: 'flex-1',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1218 },
                            },
                            React.createElement(
                              'p',
                              {
                                className: 'text-xs font-medium text-green-700',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1218 },
                              },
                              'File selected: ',
                              _optionalChain([
                                paymentProofFile,
                                'optionalAccess',
                                (_44) => _44.name,
                              ])
                            ),
                            React.createElement(
                              'p',
                              {
                                className: 'text-[10px] text-green-600',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1218 },
                              },
                              (
                                _optionalChain([
                                  paymentProofFile,
                                  'optionalAccess',
                                  (_45) => _45.size,
                                ]) / 1024
                              ).toFixed(1),
                              ' KB'
                            )
                          ),
                          React.createElement(
                            'button',
                            {
                              type: 'button',
                              onClick: () => {
                                setPaymentProof(null);
                                setPaymentProofFile(null);
                              },
                              className: 'text-xs text-red-500 hover:text-red-700',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1218 },
                            },
                            'Remove'
                          )
                        )
                      )
                    : null,
                  React.createElement(
                    'label',
                    {
                      className: `flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-sm cursor-pointer transition-all ${paymentProof ? 'border-green-400 bg-green-50' : 'border-stone-300 hover:border-gold-500 hover:bg-white'}`,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1220 },
                    },
                    React.createElement(
                      'div',
                      {
                        className:
                          'flex flex-col items-center justify-center pt-5 pb-6 text-stone-400',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1221 },
                      },
                      React.createElement(Upload, {
                        className: 'w-8 h-8 mb-2',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1221 },
                      }),
                      React.createElement(
                        'p',
                        {
                          className: 'text-xs font-medium',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1221 },
                        },
                        'Click to upload screenshot'
                      ),
                      React.createElement(
                        'p',
                        {
                          className: 'text-[10px] mt-1',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1221 },
                        },
                        'JPG, PNG up to 5MB'
                      )
                    ),
                    React.createElement('input', {
                      type: 'file',
                      className: 'hidden',
                      accept: 'image/jpeg,image/png,image/jpg',
                      onChange: handleFileUpload,
                      disabled: loadingBanks || bankDetails.length === 0,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1222 },
                    })
                  ),
                  !paymentProof &&
                    React.createElement(
                      'p',
                      {
                        className: 'text-[10px] text-red-400 mt-2 flex items-center gap-1',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1224 },
                      },
                      React.createElement(AlertCircle, {
                        size: 10,
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1224 },
                      }),
                      ' Proof of payment is required'
                    )
                )
              )
            )
          )
        ),

        React.createElement(
          'div',
          {
            className: 'lg:w-[400px] flex-shrink-0',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 1231 },
          },
          React.createElement(
            'div',
            {
              className: 'bg-white border border-stone-200 p-8 shadow-lg rounded-sm sticky top-32',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1232 },
            },
            React.createElement(
              'h3',
              {
                className: 'font-serif text-xl text-stone-900 mb-6 pb-4 border-b border-stone-100',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1233 },
              },
              'Order Summary'
            ),
            React.createElement(
              'div',
              {
                className: 'space-y-6 mb-8 max-h-[400px] overflow-y-auto scrollbar-hide',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1234 },
              },
              displayItems.map((item) => {
                // Get jewelry-specific details
                const weight = item.weight
                  ? typeof item.weight === 'number'
                    ? item.weight.toFixed(2)
                    : item.weight
                  : null;
                const karat = item.karat || null;
                const goldType = item.goldType || null;

                return React.createElement(
                  'div',
                  {
                    key: item.cart_id || item.id,
                    className: 'flex gap-4 py-4 border-b border-stone-100 last:border-0',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1242 },
                  },
                  React.createElement(
                    'div',
                    {
                      className:
                        'w-16 h-16 bg-stone-50 border border-stone-100 rounded-sm overflow-hidden flex-shrink-0 cursor-pointer',
                      onClick: () =>
                        _optionalChain([onSelectProduct, 'optionalCall', (_46) => _46(item)]),
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1243 },
                    },
                    React.createElement('img', {
                      src: repairBase64Image(item.image),
                      alt: item.name,
                      className: 'w-full h-full object-cover',
                      onError: (e) => {
                        e.target.src = PLACEHOLDER_IMAGE;
                      },
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1244 },
                    })
                  ),
                  React.createElement(
                    'div',
                    {
                      className: 'flex-1 flex flex-col justify-between',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1253 },
                    },
                    React.createElement(
                      'div',
                      { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1254 } },
                      React.createElement(
                        'div',
                        {
                          className: 'flex justify-between items-start',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1255 },
                        },
                        React.createElement(
                          'h4',
                          {
                            className:
                              'font-serif text-sm text-stone-900 line-clamp-1 cursor-pointer hover:text-gold-600 transition-colors',
                            onClick: () =>
                              _optionalChain([onSelectProduct, 'optionalCall', (_47) => _47(item)]),
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1256 },
                          },
                          item.name
                        ),
                        !propDirectCheckoutItem &&
                          onRemoveFromCart &&
                          React.createElement(
                            'button',
                            {
                              onClick: () => onRemoveFromCart(item.cart_id || item.id),
                              className: 'text-stone-300 hover:text-red-500 transition-colors',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1260 },
                            },
                            React.createElement(Trash2, {
                              size: 12,
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1261 },
                            })
                          )
                      ),
                      React.createElement(
                        'p',
                        {
                          className: 'text-[10px] text-stone-500 uppercase tracking-wide',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1265 },
                        },
                        item.category
                      ),

                      /* ADDED: Jewelry specifications display */
                      (weight || karat || goldType) &&
                        React.createElement(
                          'div',
                          {
                            className: 'flex flex-wrap gap-2 mt-1',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1269 },
                          },
                          karat &&
                            React.createElement(
                              'span',
                              {
                                className:
                                  'text-[9px] text-stone-500 bg-stone-100 px-1.5 py-0.5 rounded',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1271 },
                              },
                              karat
                            ),
                          goldType &&
                            React.createElement(
                              'span',
                              {
                                className:
                                  'text-[9px] text-stone-500 bg-stone-100 px-1.5 py-0.5 rounded',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1276 },
                              },
                              goldType
                            ),
                          weight &&
                            React.createElement(
                              'span',
                              {
                                className:
                                  'text-[9px] text-stone-500 bg-stone-100 px-1.5 py-0.5 rounded',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1281 },
                              },
                              weight,
                              'g'
                            )
                        )
                    ),
                    React.createElement(
                      'div',
                      {
                        className: 'flex justify-between items-end mt-2',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1288 },
                      },
                      React.createElement(
                        'div',
                        {
                          className: 'flex items-center gap-3',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1289 },
                        },
                        !propDirectCheckoutItem && onUpdateCartQty
                          ? React.createElement(
                              'div',
                              {
                                className: 'flex items-center border border-stone-200 rounded-sm',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1291 },
                              },
                              React.createElement(
                                'button',
                                {
                                  onClick: () => onUpdateCartQty(item.cart_id || item.id, -1),
                                  className: 'px-1.5 py-0.5 text-stone-500 hover:bg-stone-50',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1292 },
                                },
                                React.createElement(Minus, {
                                  size: 10,
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1293 },
                                })
                              ),
                              React.createElement(
                                'span',
                                {
                                  className: 'px-1.5 text-xs font-mono min-w-[20px] text-center',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1295 },
                                },
                                item.quantity
                              ),
                              React.createElement(
                                'button',
                                {
                                  onClick: () => onUpdateCartQty(item.cart_id || item.id, 1),
                                  className: 'px-1.5 py-0.5 text-stone-500 hover:bg-stone-50',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1296 },
                                },
                                React.createElement(Plus, {
                                  size: 10,
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1297 },
                                })
                              )
                            )
                          : React.createElement(
                              'span',
                              {
                                className: 'text-xs text-stone-500',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1301 },
                              },
                              'Qty: ',
                              item.quantity
                            ),
                        React.createElement(
                          'button',
                          {
                            onClick: () =>
                              _optionalChain([onSelectProduct, 'optionalCall', (_48) => _48(item)]),
                            className: 'text-[10px] font-bold text-gold-600 hover:underline',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1303 },
                          },
                          'View'
                        )
                      ),
                      React.createElement(
                        'span',
                        {
                          className: 'font-medium text-sm text-stone-900',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1307 },
                        },
                        '₱ ',
                        (item.price * item.quantity).toLocaleString()
                      )
                    ),

                    /* Stock warning */
                    item.stock !== undefined &&
                      item.stock <= 5 &&
                      item.stock > 0 &&
                      React.createElement(
                        'p',
                        {
                          className: 'text-[9px] text-amber-600 mt-1',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1314 },
                        },
                        'Only ',
                        item.stock,
                        ' left in stock'
                      )
                  )
                );
              })
            ),
            React.createElement(
              'div',
              {
                className: 'space-y-3 border-t border-stone-100 pt-6 mb-8',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1321 },
              },
              React.createElement(
                'div',
                {
                  className: 'flex justify-between text-sm',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1322 },
                },
                React.createElement(
                  'span',
                  {
                    className: 'text-stone-500',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1323 },
                  },
                  'Subtotal'
                ),
                React.createElement(
                  'span',
                  {
                    className: 'text-stone-900 font-medium',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1324 },
                  },
                  '₱ ',
                  subtotal.toLocaleString()
                )
              ),
              React.createElement(
                'div',
                {
                  className: 'flex justify-between text-sm',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1326 },
                },
                React.createElement(
                  'span',
                  {
                    className: 'text-stone-500',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1327 },
                  },
                  deliveryType === 'pickup' ? 'Pickup (Free)' : 'Shipping (Insured)'
                ),
                React.createElement(
                  'span',
                  {
                    className: 'text-emerald-600 font-bold uppercase text-xs',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1328 },
                  },
                  'Free'
                )
              ),
              React.createElement(
                'div',
                {
                  className:
                    'flex justify-between text-lg font-serif border-t border-stone-100 pt-4 mt-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1330 },
                },
                React.createElement(
                  'span',
                  {
                    className: 'text-stone-900',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1331 },
                  },
                  'Total'
                ),
                React.createElement(
                  'span',
                  {
                    className: 'text-stone-900 font-bold',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1332 },
                  },
                  '₱ ',
                  total.toLocaleString()
                )
              )
            ),
            React.createElement(
              'button',
              {
                form: 'checkout-form',
                type: 'submit',
                disabled: isProcessing || loadingBanks || bankDetails.length === 0 || !paymentProof,
                className: `w-full py-4 uppercase text-xs tracking-[0.2em] transition-colors shadow-lg flex items-center justify-center gap-2 rounded-sm ${isProcessing || loadingBanks || bankDetails.length === 0 || !paymentProof ? 'bg-stone-300 text-stone-600 cursor-not-allowed' : 'bg-stone-900 text-white hover:bg-gold-600'}`,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1335 },
              },
              isProcessing
                ? React.createElement(
                    React.Fragment,
                    null,
                    React.createElement('div', {
                      className:
                        'w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1336 },
                    }),
                    ' Processing...'
                  )
                : loadingBanks
                  ? 'Loading Bank Details...'
                  : bankDetails.length === 0
                    ? 'Bank Unavailable'
                    : !paymentProof
                      ? 'Upload Proof to Continue'
                      : 'Place Order'
            ),
            React.createElement(
              'div',
              {
                className:
                  'mt-6 flex items-center justify-center gap-2 text-[10px] text-stone-400 uppercase tracking-widest',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1338 },
              },
              React.createElement(Lock, {
                size: 10,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1339 },
              }),
              ' Secure SSL Encrypted'
            )
          )
        )
      )
    )
  );
};

export default Checkout;
